import type { WeddingSummary } from './schemas/wedding.js';

/**
 * The getting-started list for a new couple.
 *
 * **Derived, never stored.** A checklist with its own rows can disagree with
 * reality — ticked when the thing was undone, or unticked when it was done
 * somewhere else — and then it is a second thing to maintain that is wrong. Every
 * step here is computed from the wedding and its summary, so adding a guest ticks
 * "add your guests" whatever route the guest came in by.
 *
 * It is also why there is no dismiss: the list disappears when it is finished,
 * which is the only honest way for a derived list to go away.
 */

export interface OnboardingStep {
  key: string;
  /** Which tab completes it, for the link. */
  tab: 'home' | 'guests' | 'venues' | 'vendors' | 'budget' | 'timeline' | 'site' | 'seating';
  done: boolean;
}

export interface OnboardingState {
  steps: OnboardingStep[];
  done: number;
  total: number;
  /** True once every step is done; the caller stops showing it. */
  complete: boolean;
}

export interface OnboardingInput {
  hasDate: boolean;
  /** More than the person who created it. */
  hasPartner: boolean;
  summary: Pick<WeddingSummary, 'guests' | 'venues' | 'budget' | 'tasks'>;
}

export function onboardingState(input: OnboardingInput): OnboardingState {
  const steps: OnboardingStep[] = [
    // The date first, because four other things are computed from it and the
    // checklist cannot even be generated without one.
    { key: 'date', tab: 'home', done: input.hasDate },
    { key: 'partner', tab: 'home', done: input.hasPartner },
    { key: 'guests', tab: 'guests', done: input.summary.guests.invited > 0 },
    { key: 'venue', tab: 'venues', done: input.summary.venues.total > 0 },
    { key: 'budget', tab: 'budget', done: input.summary.budget.plannedCents > 0 },
    { key: 'checklist', tab: 'timeline', done: input.summary.tasks.open > 0 },
  ];
  const done = steps.filter((s) => s.done).length;
  return { steps, done, total: steps.length, complete: done === steps.length };
}
